import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import './LoginSignup.css';

// --- ASSETS & ICONS ---
// Updated image imports to be used in the component
import loginImage from './../../assets/img/101.png';
import signupImage from './../../assets/img/102.png';

// --- UI COMPONENTS (Simplified for OTP Flow) ---
const SpinnerIcon = React.memo(() => <svg className="nfc-contact-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>);
const CtaButton = React.memo(({ children, isLoading, disabled, ...props }) => ( <motion.button className="nfc-contact-cta-button" whileHover={{ y: -3, boxShadow: "0 7px 20px rgba(0, 82, 212, 0.25)" }} whileTap={{ y: -1, scale: 0.98 }} transition={{ type: "spring", stiffness: 400, damping: 17 }} disabled={isLoading || disabled} {...props}>{isLoading ? <SpinnerIcon /> : children}</motion.button> ));
const InputField = React.memo(({ id, label, type, value, onChange, isRequired = true }) => {
    return (
        <div className="nfc-contact-input-group">
            <input type={type} id={id} name={id} value={value} onChange={onChange} placeholder=" " className="nfc-contact-input-field" required={isRequired} autoComplete="off" />
            <label htmlFor={id} className="nfc-contact-input-label">{label}</label>
        </div>
    );
});


// --- MAIN COMPONENT (Formerly NfcContactForm) ---
export default function LoginSignup() {
    // --- STATE MANAGEMENT ---
    const [isLogin, setIsLogin] = useState(true);
    const [step, setStep] = useState(1); // 1 for user details, 2 for OTP
    const [isLoading, setIsLoading] = useState(false);
    
    // Form fields state
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    
    // Backend API URL
const REACT_APP_TEST = process.env.REACT_APP_TEST || 'https://api.nownfc.com:5007';

// const REACT_APP_TEST =  process.env.REACT_APP_TEST;

    // --- FORM ACTIONS ---

    // Handles the first step: sending OTP for login or registering
    const handlePrimaryAction = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const endpoint = isLogin ? `${REACT_APP_TEST}/api/auth/otp/send` : `${REACT_APP_TEST}/api/auth/signup`;
        const payload = isLogin ? { phone } : { name, phone, email };

        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            
            Swal.fire({ icon: 'success', title: 'Success!', text: data.message });
            setStep(2); // Move to OTP entry step

        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: error.message || 'An unknown error occurred.' });
        } finally {
            setIsLoading(false);
        }
    };

    // Handles the second step: verifying the OTP
    const handleOtpVerification = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const res = await fetch(`${REACT_APP_TEST}/api/auth/verify-otp-and-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone, otp }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            
            Swal.fire({ icon: 'success', title: 'Login Successful!', text: `Welcome, ${data.user.name}!` });
            localStorage.setItem('authToken', data.token);
            
            setTimeout(() => { window.location.href = '/'; }, 1500);

        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Verification Failed', text: error.message });
        } finally {
            setIsLoading(false);
        }
    };

    // Toggles between Login and Signup mode
    const toggleFormMode = (e) => {
        e.preventDefault();
        setIsLogin(!isLogin);
        setStep(1);
        setName('');
        setPhone('');
        setEmail('');
        setOtp('');
    };

    // Animation variants for Framer Motion
    const formVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
        exit: { opacity: 0, y: -20, transition: { duration: 0.3, ease: "easeIn" } }
    };

    return (
        <main className="nfc-contact-container">
            <div className="nfc-contact-background-container" />
            <div className="nfc-contact-page-container">
                <div className="nfc-contact-login-container">
                    <div className="nfc-contact-brand-section">
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={isLogin ? 'login-img' : 'signup-img'}
                                src={isLogin ? loginImage : signupImage}
                                alt={isLogin ? "Abstract art for login" : "People working for signup"}
                                className="nfc-contact-welcome-image"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5, ease: 'easeInOut' }}
                            />
                        </AnimatePresence>
                    </div>

                    <div className="nfc-contact-form-section">
                        <AnimatePresence mode="wait">
                            {step === 1 ? (
                                <motion.div className="nfc-contact-form-content-wrapper" key="details-step" variants={formVariants} initial="hidden" animate="visible" exit="exit">
                                    <h1 className="nfc-contact-form-greeting">{isLogin ? 'Welcome Back!' : 'Create Your Account'}</h1>
                                    <p className="nfc-contact-form-subtitle">{isLogin ? 'Sign in with your registered phone number.' : 'Get started with just your phone number.'}</p>
                                    
                                    <form onSubmit={handlePrimaryAction} noValidate>
                                        {!isLogin && (
                                            <InputField id="name" label="Full Name" type="text" value={name} onChange={(e) => setName(e.target.value)} isRequired={true} />
                                        )}
                                        <InputField id="phone" label="10-Digit Phone Number" type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} isRequired={true} />
                                        {!isLogin && (
                                            <InputField id="email" label="Email (Optional)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} isRequired={false} />
                                        )}
                                        <div className="nfc-contact-form-submit-group">
                                            <CtaButton type="submit" isLoading={isLoading}>{isLogin ? 'Send OTP' : 'Register & Send OTP'}</CtaButton>
                                        </div>
                                    </form>

                                    <div className="nfc-contact-form-switcher">
                                        <p>{isLogin ? "Don't have an account?" : "Already a member?"} <a href="#" onClick={toggleFormMode} className="nfc-contact-switcher-link">{isLogin ? 'Sign Up' : 'Sign In'}</a></p>
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div className="nfc-contact-form-content-wrapper" key="otp-step" variants={formVariants} initial="hidden" animate="visible" exit="exit">
                                    <h1 className="nfc-contact-form-greeting">Verify Your Phone</h1>
                                    <p className="nfc-contact-form-subtitle">An OTP was sent to +91 {phone}. Please enter it below.</p>
                                    
                                    <form onSubmit={handleOtpVerification} noValidate>
                                        <InputField id="otp" label="6-Digit OTP" type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} isRequired={true} />
                                        <div className="nfc-contact-form-submit-group">
                                            <CtaButton type="submit" isLoading={isLoading}>Verify & Login</CtaButton>
                                        </div>
                                    </form>

                                    <div className="nfc-contact-form-switcher">
                                        <p>Entered the wrong number? <a href="#" onClick={() => setStep(1)} className="nfc-contact-switcher-link">Go Back</a></p>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </main>
    );
};